import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { lookup } from "../lib/strings";

// Iter 50 — Language toggle (English / Hinglish).
// Mirrors ThemeContext's persistence pattern. Stored under `bt_lang` so it
// doesn't collide with theme or other preferences.
const LanguageContext = createContext({
  language: "en",
  setLanguage: () => {},
  toggleLanguage: () => {},
  t: (k) => k,
});

const STORAGE_KEY = "bt_lang";
const VALID = new Set(["en", "hi"]);

function getInitialLanguage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (VALID.has(saved)) return saved;
  } catch { /* private mode → fall through */ }
  return "en";
}

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, language); } catch { /* noop */ }
    document.documentElement.setAttribute("data-lang", language);
  }, [language]);

  const value = useMemo(() => ({
    language,
    setLanguage: (l) => VALID.has(l) && setLanguage(l),
    toggleLanguage: () => setLanguage((l) => (l === "en" ? "hi" : "en")),
    // `t(key)` reads from the strings table; falls back to English then to
    // the raw key so missing translations never break the UI.
    t: (key) => lookup(key, language),
  }), [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
